import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { GLYPH_GAP } from '../render/ActivityFeed';

/**
 * How much of a question the bubble shows before it collapses. A pasted
 * document arrives as one message and a CSS clamp still lays out every glyph it
 * hides, so the tail is kept out of the DOM until asked for. Copy reads the full
 * text either way.
 */
const COLLAPSE_OVER_CHARS = 900;

/** What a collapsed bubble shows. Derived, so it cannot rise above the
 *  threshold and leave the toggle revealing nothing. */
const COLLAPSED_CHARS = COLLAPSE_OVER_CHARS - 200;

/**
 * A user's question, right-aligned bubble. Hovering reveals a Copy button so
 * the question text can be grabbed to re-ask or tweak (selecting inside a chat
 * bubble is fiddly). Text is explicitly selectable too.
 */
export function UserMsg({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Count and cut by code point: a pasted document carries emoji, and slicing
  // UTF-16 code units halves a surrogate pair into a replacement character.
  const chars = useMemo(() => [...text], [text]);
  const collapsible = chars.length > COLLAPSE_OVER_CHARS;
  const shown = collapsible && !expanded ? `${chars.slice(0, COLLAPSED_CHARS).join('').trimEnd()}…` : text;

  // Reset the "Copied" confirmation after a beat; self-clears on unmount.
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(id);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard blocked (permissions / insecure context) — leave the text
      // selectable so the user can copy it by hand.
    }
  }

  return (
    <div className="he-usermsg" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, marginBottom: 4 }}>
      <div
        style={{
          background: 'var(--he-purple)',
          color: '#fff',
          padding: '12px 18px',
          borderRadius: '18px 18px 4px 18px',
          fontSize: 15.5,
          lineHeight: 1.5,
          maxWidth: '76%',
          fontWeight: 500,
          userSelect: 'text',
          whiteSpace: 'pre-wrap',
          // A paste can be one unbroken token (base64, a URL, minified text),
          // which pre-wrap has nowhere to break and would run past the bubble.
          overflowWrap: 'anywhere',
        }}
      >
        {shown}
        {collapsible && (
          <>
            {'\n'}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="he-focusable he-focusable-on-brand"
              aria-expanded={expanded}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px 0 0',
                cursor: 'pointer',
                color: '#fff',
                opacity: 0.85,
                fontSize: 13,
                fontFamily: 'inherit',
                fontWeight: 600,
                textDecoration: 'underline',
              }}
            >
              {expanded ? 'Show less' : `Show all ${chars.length.toLocaleString()} characters`}
            </button>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={copy}
        className="he-copybtn he-focusable"
        aria-label={copied ? 'Copied' : 'Copy message'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          cursor: 'pointer',
          color: 'var(--fg-3)',
          fontSize: 12,
          fontFamily: 'inherit',
        }}
      >
        {copied ? (
          <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Copied
          </>
        ) : (
          <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M5 15V5a2 2 0 0 1 2-2h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            Copy
          </>
        )}
      </button>
    </div>
  );
}

/**
 * "Report a problem" under an answer that is no longer streaming: quiet, and
 * always visible. An
 * answer has no hovered container, so the `.he-copybtn` treatment the question's
 * Copy button uses would leave this permanently invisible.
 */
export function ReportAnswer({ onReport }: { onReport: () => void }) {
  return (
    <div style={{ display: 'flex', marginTop: 6 }}>
      <button
        type="button"
        onClick={onReport}
        className="he-reportbtn he-focusable"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          cursor: 'pointer',
          fontSize: 12,
          fontFamily: 'inherit',
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 7.5v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="16.6" r="1" fill="currentColor" />
        </svg>
        Report a problem
      </button>
    </div>
  );
}

export function Thinking() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: GLYPH_GAP, color: 'var(--fg-2)', fontSize: 14.5 }}>
      <span className="he-dots">
        <i />
        <i />
        <i />
      </span>
      Analyzing network data…
    </div>
  );
}

/**
 * Loading placeholder shown while awaiting the first streamed part of an answer:
 * the "Analyzing…" indicator over a few pulsing lines + a card block, so the
 * turn has shape before content arrives. Generic (message-shaped) rather than
 * chart-shaped — the streaming model doesn't know a block's type in advance.
 */
export function ChatSkeleton() {
  return (
    <div className="he-fadein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Thinking />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <div className="he-skel" style={{ height: 13, width: '92%' }} />
        <div className="he-skel" style={{ height: 13, width: '78%' }} />
        <div className="he-skel" style={{ height: 13, width: '85%' }} />
      </div>
      <div className="he-skel" style={{ height: 128, width: '100%', borderRadius: 18 }} />
    </div>
  );
}

/**
 * Persistent in-chat sign that the turn is still running, for the stretch after
 * the answer has visible content and the skeleton has gone. Without it the pane
 * can show what looks like a finished answer — or a lull between blocks — with
 * nothing saying more is coming, while the sidebar's "Active" row says it is.
 * Deliberately quiet: a live dot plus a few words, so it reads as status rather
 * than as another block. `role="status"` marks it as a status region rather than
 * content, which is what it is; whether a screen reader voices a region inserted
 * with its text already in place varies by reader, so this is a correct label and
 * not a guarantee of an announcement.
 */
export function StillWorking() {
  return (
    <div
      className="he-fadein"
      role="status"
      style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--fg-2)', fontSize: 14.5 }}
    >
      <span className="he-livedot" />
      Still working on this…
    </div>
  );
}

/**
 * End-of-thread notice for a conversation whose session is closed: eve rejects
 * every follow-up to it, so this replaces the retryable "couldn't reach" copy
 * and offers the only way forward. Cause-neutral on purpose, since a reset, the
 * session deadline, and a runtime upgrade all land here and the UI can't tell
 * which. `onContinue` is optional so the notice still reads correctly on a
 * surface that has nowhere to continue to.
 */
export function ClosedNotice({ onContinue, hasPage }: { onContinue?: () => void; hasPage?: boolean }) {
  return (
    <div style={{ color: 'var(--fg-2)', fontSize: 14.5 }}>
      This conversation has closed and can’t take new messages. Everything above is still here to
      read or copy.
      {/* A page outlives the conversation that made it: it belongs to whoever created
          it, so a new chat can still change it. Only said when this thread actually
          published one, or it reads as noise on the many threads that didn't. */}
      {hasPage && ' Pages you published here belong to you, not to this conversation — open one from the sidebar to keep editing it.'}
      {onContinue && <NoticeAction onClick={onContinue}>Continue in a new chat</NoticeAction>}
    </div>
  );
}

/**
 * End-of-thread notice for a send the agent refused to parse, which means this
 * tab is running a bundle from before a wire-protocol change (see
 * `isProtocolRejection`). Retrying can only repeat the rejection, so the way
 * forward is a reload. The question is still rendered above with its own Copy
 * button, so the copy points there rather than trying to carry the text across
 * the reload.
 */
export function OutdatedNotice() {
  return (
    <div style={{ color: 'var(--fg-2)', fontSize: 14.5 }}>
      Helium AI has been updated and this tab is running the older version, so that didn’t go
      through. Reload to continue. You can copy your question above first.
      <NoticeAction onClick={() => window.location.reload()}>Reload</NoticeAction>
    </div>
  );
}

/** The single action offered under an end-of-thread notice: text-weight, inline, no chrome. */
function NoticeAction({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          font: 'inherit',
          color: 'var(--he-purple)',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        {children}
      </button>
    </div>
  );
}
